import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";

interface Payout {
 _id: string;
 amount: number;
 currency: string;
 status: "pending" | "processing" | "completed" | "failed" | "cancelled";
 payoutMethod: string;
 periodStart: string;
 periodEnd: string;
 createdAt: string;
 processedAt?: string;
}

interface PayoutSummary {
 available: number;
 pending: number;
 totalPaid: number;
 currency: string;
 nextPayoutDate?: string;
 lastPayoutDate?: string;
 payoutSchedule?: string;
 holdPeriodDays: number;
}

const StorePayouts: React.FC = () => {
 const { t } = useTranslation();
 const { isAuthenticated } = usePlatformAuth();
 const navigate = useNavigate();
 const { storeId } = useParams<{ storeId: string }>();
 const [summary, setSummary] = useState<PayoutSummary | null>(null);
 const [payouts, setPayouts] = useState<Payout[]>([]);
 const [loading, setLoading] = useState(true);
 const [creatingPayout, setCreatingPayout] = useState(false);
 const [payoutSettings, setPayoutSettings] = useState({
  payoutSchedule: "",
  payoutHoldPeriodDays: 7,
 });
 const [updatingSettings, setUpdatingSettings] = useState(false);

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  if (storeId) {
   fetchData();
  }
 }, [isAuthenticated, navigate, storeId]);

 const fetchData = async () => {
  try {
   setLoading(true);
   const [summaryResponse, payoutsResponse, storeResponse] = await Promise.all([
    platformApiService.getStorePayoutSummary(storeId!),
    platformApiService.getPayouts({ storeId, limit: 50 }),
    platformApiService.getStore(storeId!),
   ]);

   setSummary(summaryResponse.data.summary);
   setPayouts(payoutsResponse.data.payouts || []);
   
   const store = storeResponse.data.store;
   setPayoutSettings({
    payoutSchedule: store.payoutSchedule || "",
    payoutHoldPeriodDays: store.payoutHoldPeriodDays || 7,
   });
  } catch (error) {
   console.error("Error fetching data:", error);
  } finally {
   setLoading(false);
  }
 };

 const handleCreatePayout = async () => {
  if (!window.confirm("Create a payout for available funds?")) {
   return;
  }

  try {
   setCreatingPayout(true);
   const periodEnd = new Date();
   const periodStart = new Date();
   
   // Default to last 30 days if no previous payout
   if (summary?.lastPayoutDate) {
    periodStart.setTime(new Date(summary.lastPayoutDate).getTime());
   } else {
    periodStart.setDate(periodStart.getDate() - 30);
   }

   await platformApiService.createPayout({
    storeId: storeId!,
    periodStart: periodStart.toISOString(),
    periodEnd: periodEnd.toISOString(),
   });

   fetchData();
   alert("Payout created successfully");
  } catch (error: any) {
   console.error("Error creating payout:", error);
   alert(error.response?.data?.error || "Failed to create payout");
  } finally {
   setCreatingPayout(false);
  }
 };

 const handleUpdateSettings = async () => {
  try {
   setUpdatingSettings(true);
   await platformApiService.updateStorePayoutSettings(storeId!, payoutSettings);
   fetchData();
   alert("Settings updated successfully");
  } catch (error: any) {
   console.error("Error updating settings:", error);
   alert(error.response?.data?.error || "Failed to update settings");
  } finally {
   setUpdatingSettings(false);
  }
 };

 const getStatusColor = (status: string) => {
  switch (status) {
   case "completed":
    return "bg-green-100 text-green-800";
   case "pending":
    return "bg-yellow-100 text-yellow-800";
   case "processing":
    return "bg-blue-100 text-blue-800";
   case "failed":
    return "bg-red-100 text-red-800";
   case "cancelled":
    return "bg-gray-100 text-gray-800";
   default:
    return "bg-gray-100 text-gray-800";
  }
 };

 if (loading) {
  return <div className="p-8">Loading...</div>;
 }

 if (!summary) {
  return <div className="p-8">{t("notFound.storeNotFound")}</div>;
 }

 return (
  <div className="p-8">
   <div className="mb-6">
    <Link
     to="/platform/stores"
     className="text-indigo-600 hover:text-indigo-900 mb-4 inline-block"
    >
     ← Back to Stores
    </Link>
    <h1 className="text-3xl font-bold">Store Payouts</h1>
   </div>

   {/* Summary Cards */}
   <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
    <div className="bg-white shadow p-4">
     <div className="text-sm text-gray-500">Available Funds</div>
     <div className="text-2xl font-bold text-green-600">
      {summary.currency} {summary.available.toFixed(2)}
     </div>
    </div>
    <div className="bg-white shadow p-4">
     <div className="text-sm text-gray-500">Pending Funds</div>
     <div className="text-2xl font-bold text-yellow-600">
      {summary.currency} {summary.pending.toFixed(2)}
     </div>
    </div>
    <div className="bg-white shadow p-4">
     <div className="text-sm text-gray-500">Total Paid</div>
     <div className="text-2xl font-bold">
      {summary.currency} {summary.totalPaid.toFixed(2)}
     </div>
    </div>
    <div className="bg-white shadow p-4">
     <div className="text-sm text-gray-500">Hold Period</div>
     <div className="text-2xl font-bold">{summary.holdPeriodDays} days</div>
    </div>
   </div>

   <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    {/* Main Content */}
    <div className="lg:col-span-2 space-y-6">
     {/* Create Payout */}
     {summary.available > 0 && (
      <div className="bg-white shadow p-6">
       <h2 className="text-xl font-bold mb-4">Create Payout</h2>
       <p className="text-gray-600 mb-4">
        Available funds: {summary.currency} {summary.available.toFixed(2)}
       </p>
       <button
        onClick={handleCreatePayout}
        disabled={creatingPayout}
        className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
       >
        {creatingPayout ? "Creating..." : "Create Payout"}
       </button>
      </div>
     )}

     {/* Payout History */}
     <div className="bg-white shadow p-6">
      <h2 className="text-xl font-bold mb-4">Payout History</h2>
      {payouts.length === 0 ? (
       <div className="text-center py-8 text-gray-500">No payouts yet</div>
      ) : (
       <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
         <thead className="bg-gray-50">
          <tr>
           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            Amount
           </th>
           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            Status
           </th>
           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            Method
           </th>
           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            Period
           </th>
           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            Created
           </th>
           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            Actions
           </th>
          </tr>
         </thead>
         <tbody className="bg-white divide-y divide-gray-200">
          {payouts.map((payout) => (
           <tr key={payout._id}>
            <td className="px-4 py-3 whitespace-nowrap font-medium">
             {payout.currency} {payout.amount.toFixed(2)}
            </td>
            <td className="px-4 py-3 whitespace-nowrap">
             <span
              className={`px-2 py-1 text-xs font-semibold ${getStatusColor(
               payout.status
              )}`}
             >
              {payout.status}
             </span>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
             {payout.payoutMethod}
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
             {new Date(payout.periodStart).toLocaleDateString()} -{" "}
             {new Date(payout.periodEnd).toLocaleDateString()}
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
             {new Date(payout.createdAt).toLocaleDateString()}
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm">
             <Link
              to={`/platform/payouts/${payout._id}`}
              className="text-indigo-600 hover:text-indigo-900"
             >
              View
             </Link>
            </td>
           </tr>
          ))}
         </tbody>
        </table>
       </div>
      )}
     </div>
    </div>

    {/* Settings Sidebar */}
    <div className="space-y-6">
     <div className="bg-white shadow p-6">
      <h2 className="text-xl font-bold mb-4">Payout Settings</h2>
      <div className="space-y-4">
       <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
         Payout Schedule
        </label>
        <select
         value={payoutSettings.payoutSchedule}
         onChange={(e) =>
          setPayoutSettings({ ...payoutSettings, payoutSchedule: e.target.value })
         }
         className="w-full px-3 py-2 border border-gray-300 "
        >
         <option value="">Not Set</option>
         <option value="weekly">Weekly</option>
         <option value="biweekly">Bi-weekly</option>
         <option value="monthly">Monthly</option>
        </select>
       </div>
       <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
         Hold Period (Days)
        </label>
        <input
         type="number"
         min="0"
         value={payoutSettings.payoutHoldPeriodDays}
         onChange={(e) =>
          setPayoutSettings({
           ...payoutSettings,
           payoutHoldPeriodDays: parseInt(e.target.value) || 0,
          })
         }
         className="w-full px-3 py-2 border border-gray-300 "
        />
       </div>
       <button
        onClick={handleUpdateSettings}
        disabled={updatingSettings}
        className="w-full px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
       >
        {updatingSettings ? "Updating..." : "Update Settings"}
       </button>
      </div>
     </div>

     {/* Next Payout Info */}
     {summary.nextPayoutDate && (
      <div className="bg-white shadow p-6">
       <h2 className="text-xl font-bold mb-4">Next Payout</h2>
       <div className="text-sm text-gray-600">
        <div className="mb-2">
         <span className="font-medium">Scheduled:</span>{" "}
         {new Date(summary.nextPayoutDate).toLocaleDateString()}
        </div>
        {summary.payoutSchedule && (
         <div>
          <span className="font-medium">Frequency:</span> {summary.payoutSchedule}
         </div>
        )}
       </div>
      </div>
     )}
    </div>
   </div>
  </div>
 );
};

export default StorePayouts;

